import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, AfterViewInit, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { HttpClient, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { EMPTY, Subject, Subscription, of, timer } from 'rxjs';
import { catchError, debounce, distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  calculateLocalStockForDisplay,
  resolveWarehouseStockField
} from '../../item-details/item-utils';

@Component({
  selector: 'app-item-browse-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule, TableModule, HttpClientModule, IconFieldModule, InputIconModule],
  templateUrl: './item-browse-popup.component.html',
  styleUrls: ['./item-browse-popup.component.scss']
})
export class ItemBrowsePopupComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @ViewChild('itemSearchInput', { static: false }) itemSearchInput?: ElementRef<HTMLInputElement>;

  @Input() visible: boolean = false;
  @Input() searchText: string = '';
  @Input() cono: string | number = '';
  @Input() divi: string | number = '';
  @Input() warehouse: string | number = '';
  @Input() itemList: any[] = [];
  @Input() selectedRow: any = null;
  searchResults: any[] = [];
  isLoading: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() searchTextChange = new EventEmitter<string>();
  @Output() rowSelected = new EventEmitter<any>();
  @Output() selected = new EventEmitter<any>();

  private searchText$ = new Subject<string>();
  private searchSubscription: Subscription | null = null;
  private searchRequestId = 0;

  onClose() {
    this.isLoading = false;
    this.visibleChange.emit(false);
  }

  constructor(private http: HttpClient) {}

  ngAfterViewInit() {
    this.focusSearchInput();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      setTimeout(() => this.focusSearchInput(), 0);
    }
  }

  private focusSearchInput(): void {
    if (this.visible && this.itemSearchInput?.nativeElement) {
      try {
        const el = this.itemSearchInput.nativeElement;
        const cursorPos = el.value?.length ?? 0;
        el.focus();
        try {
          if (typeof el.setSelectionRange === 'function') {
            el.setSelectionRange(cursorPos, cursorPos);
          }
        } catch (selErr) {
          // ignore selection errors
        }
      } catch (err) {
        // ignore focus failures on hidden inputs
      }
    }
  }

  /** Keep cursor in the browse search box after async table updates. */
  private preserveSearchInputFocus(): void {
    if (!this.visible) {
      return;
    }
    requestAnimationFrame(() => this.focusSearchInput());
  }

  ngOnInit() {
    this.searchSubscription = this.searchText$
      .pipe(
        map((text) => ('' + text).trim()),
        distinctUntilChanged(),
        debounce((text) => (text.length < 3 ? timer(0) : timer(400))),
        switchMap((text) => {
          if (text.length < 3) {
            this.searchResults = [];
            this.selectedRow = null;
            this.isLoading = false;
            return EMPTY;
          }
          const requestId = ++this.searchRequestId;
          const sqry = encodeURIComponent(this.buildSearchQuery(text));
          const cono = encodeURIComponent('' + this.cono);
          const divi = encodeURIComponent('' + this.divi);
          const wh = encodeURIComponent('' + this.warehouse);
          const base = environment.apiUrl || '';
          const endpoint = this.resolveF4SearchEndpoint(this.divi);
          const whStatField = this.resolveWarehouseStatusField(this.warehouse);
          console.log(`[ItemBrowsePopup] F4 status check using MMSTAT and ${whStatField} for warehouse ${this.warehouse}`);
          const url = `${base}${endpoint}?SQRY=${sqry}&cono=${cono}&divi=${divi}&warehouse=${wh}`;
          this.isLoading = true;
          return this.http.get<any[]>(url, { headers: this.getAuthHeaders() }).pipe(
            catchError((err) => {
              console.error('F4 search failed', err);
              this.searchResults = [];
              this.selectedRow = null;
              return of([]);
            }),
            finalize(() => {
              if (requestId === this.searchRequestId) {
                this.isLoading = false;
                this.preserveSearchInputFocus();
              }
            })
          );
        })
      )
      .subscribe({
        next: (data: any[]) => {
          this.searchResults = (data || []).map((it: any) => this.mapApiItemToRow(it));
          if (this.selectedRow && !this.searchResults.some((r) => r.itemNumber === this.selectedRow?.itemNumber)) {
            this.selectedRow = null;
          }
          this.preserveSearchInputFocus();
        },
        error: () => {
          this.searchResults = [];
          this.selectedRow = null;
        }
      });
  }

  ngOnDestroy() {
    this.isLoading = false;
    this.searchSubscription?.unsubscribe();
  }

  onSearchTextChanged(value: string) {
    this.searchTextChange.emit(value);
    this.searchText = value;
    this.searchText$.next(value);
  }

  onDialogShow() {
    setTimeout(() => this.focusSearchInput(), 50);
  }

  onSearch() {
    const term = ('' + this.searchText).trim();
    if (term.length < 3) {
      return;
    }
    this.searchText$.next(term);
  }

  onEnterKey() {
    if (this.selectedRow) {
      this.onSelect();
      return;
    }
    this.onSearch();
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
    if (token) {
      console.debug('[ItemBrowsePopup] Attaching Authorization header', token ? token.slice(0, 12) + '...' : 'no-token');
      return new HttpHeaders({ Authorization: `Bearer ${token}` });
    }
    return new HttpHeaders();
  }

  private buildSearchQuery(text: string): string {
    const cleaned = text.trim();
    const quoted = `\"${cleaned}*\"`;
    return `related:[ITEM_SEARCH(${quoted})]`;
  }

  private resolveF4SearchEndpoint(divi: string | number): string {
    const div = ('' + divi).trim();
    if (div === '400') {
      return 'm3/item/search/f4nl/query';
    }
    if (div === '300') {
      return 'm3/item/search/f4fr/query';
    }
    return 'm3/item/search/f4pl/query';
  }

  private resolveWarehouseStatusField(warehouse: string | number | null | undefined): string {
    const code = ('' + (warehouse ?? '')).trim();
    switch (code) {
      case '101': return 'MBSTAT';
      case '110': return 'MASTAT';
      case '130': return 'MCSTAT';
      case '300': return 'FBSTAT';
      case '400':
      case '450': return 'NBSTAT';
      default: return 'MBSTAT';
    }
  }

  get isDiv300(): boolean {
    return ('' + this.divi).trim() === '300';
  }

  get isDiv100(): boolean {
    return ('' + this.divi).trim() === '100';
  }

  get isDiv400(): boolean {
    return ('' + this.divi).trim() === '400';
  }

  get warehouseCode(): string {
    return ('' + this.warehouse).trim();
  }

  get isWarehouse101(): boolean {
    return this.isDiv100 && this.warehouseCode === '101';
  }

  get isWarehouse110(): boolean {
    return this.isDiv100 && this.warehouseCode === '110';
  }

  get isWarehouse130(): boolean {
    return this.isDiv100 && this.warehouseCode === '130';
  }

  get showRiedst(): boolean {
    return this.isWarehouse101;
  }

  get showMunche(): boolean {
    return this.isWarehouse110;
  }

  get showBerlin(): boolean {
    return this.isWarehouse130;
  }

  get showGftOrly(): boolean {
    return this.isDiv300 && this.warehouseCode === '300';
  }

  get showGftAmsterdam(): boolean {
    return this.isDiv400 && this.warehouseCode === '400';
  }

  get showStockColumn(): boolean {
    return this.showRiedst || this.showMunche || this.showBerlin || this.showGftOrly || this.showGftAmsterdam;
  }

  get stockColumnLabel(): string {
    if (this.showRiedst) return 'Riedst';
    if (this.showMunche) return 'Münche';
    if (this.showBerlin) return 'Berlin';
    if (this.showGftOrly) return 'GFT Orly';
    if (this.showGftAmsterdam) return 'GFT Amsterdam';
    return 'Stock';
  }

  get showPriceLists(): boolean {
    return this.isDiv100;
  }

  get searchHint(): string {
    const term = ('' + this.searchText).trim();
    if (this.isLoading) {
      return 'Searching...';
    }
    if (term.length > 0 && term.length < 3) {
      return 'Type at least 3 characters to search';
    }
    if (term.length >= 3 && this.searchResults.length === 0) {
      return 'No items found';
    }
    if (this.searchResults.length > 0) {
      return `${this.searchResults.length} item${this.searchResults.length === 1 ? '' : 's'} found`;
    }
    return 'Search by item number or description';
  }

  get emptyStateMessage(): string {
    const term = ('' + this.searchText).trim();
    if (this.isLoading) {
      return 'Searching items...';
    }
    if (term.length > 0 && term.length < 3) {
      return 'Type at least 3 characters to search';
    }
    if (term.length >= 3) {
      return 'No items found for your search';
    }
    return 'No items loaded. Use search to load results.';
  }

  get headerColspan(): number {
    let cols = 4;
    if (this.showStockColumn) {
      cols += 1;
    }
    if (this.isDiv300) {
      cols += 3;
    }
    if (this.isDiv400) {
      cols += 3;
    }
    if (this.showPriceLists) {
      cols += 3;
    }
    return cols;
  }

  private parseStockValue(value: unknown): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  private resolveAdjustedStock(it: any, itemNumber: string): number | null {
    const stockField = resolveWarehouseStockField(this.warehouse);
    if (!stockField) {
      return null;
    }
    const apiStock = this.parseStockValue(it?.[stockField]);
    const localStock = calculateLocalStockForDisplay(this.itemList, itemNumber);
    return Math.max(0, apiStock - localStock);
  }

  private mapApiItemToRow(it: any) {
    const div = ('' + this.divi).trim();
    const itemNumber = it?.MMITNO || '';
    const adjustedStock = this.resolveAdjustedStock(it, itemNumber);
    const stockDisplay = adjustedStock !== null ? adjustedStock : '';

    const common = {
      itmno: itemNumber,
      itemNumber,
      description2: it?.MMFUDS || '',
      description: it?.MMFUDS || '',
      inhalt: it?.F1A121 || '',
      stk: it?.F1N296 || '',
      stockQty: this.showStockColumn ? stockDisplay : '',
      raw: it
    } as any;

    if (div === '300') {
      return {
        ...common,
        fr1: it?.O4SAPR || '',
        fr2: it?.F2SAPR || it?.O2SAPR || '',
        fr3: it?.F3SAPR || it?.O3SAPR || ''
      };
    }

    if (div === '400') {
      return {
        ...common,
        nl1: it?.N1SAPR || '',
        nl2: it?.N2SAPR || '',
        nl3: it?.N3SAPR || ''
      };
    }

    if (div === '100') {
      return {
        ...common,
        pl1Price: it?.ODSAPR || '',
        pl2Price: it?.O2SAPR || '',
        pl3Price: it?.O3SAPR || ''
      };
    }

    return {
      ...common,
      pl1Price: it?.ODSAPR || it?.O4SAPR || '',
      pl2Price: it?.O2SAPR || it?.F2SAPR || '',
      pl3Price: it?.O3SAPR || it?.F3SAPR || ''
    };
  }

  onRowClick(row: any) {
    this.selectedRow = row;
    this.rowSelected.emit(row);
  }

  onRowDoubleClick(row: any) {
    if (this.isLoading || !row) {
      return;
    }
    this.onRowClick(row);
    this.onSelect();
  }

  onSelect() {
    if (this.isLoading || !this.selectedRow) {
      return;
    }
    this.selected.emit(this.selectedRow);
    this.onClose();
  }
}
